import { createContext, useContext, useEffect, useState } from 'react';
import {API as api} from '../services/api';
import { loginWithGoogleIdToken } from '../services/googleAuth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);   
  const [error, setError] = useState(null);
  useEffect(() => {
    const init = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await api.get('/users/me');
        setUser(res.data); // your getMe returns req.user directly
        setError(null);
      } catch (err) {
        console.error('Auth init error', err);
        // invalid/expired token
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [token]);


  const setSessionFromToken = (jwt, loggedUser) => {
    if (jwt) {
      localStorage.setItem('auth_token', jwt);
      setToken(jwt);
    } else {
      localStorage.removeItem('auth_token');
      setToken(null);
    }
    setUser(loggedUser || null);
    setError(null);
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const res = await api.post('/auth/login', { email, password });
      const { token: jwt, user: loggedUser } = res.data;

      localStorage.setItem('auth_token', jwt);
      setToken(jwt);
      setUser(loggedUser);

      return { ok: true, user: loggedUser };
    } catch (err) {
      console.error('Login error:', err);
      setError(
        err.response?.data?.message ||
        'Login failed. Please check your credentials.'
      );
      return { ok: false, error: err };
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      setError(null);
      const { token: jwt, user: loggedUser } = await loginWithGoogleIdToken(idToken);

      setSessionFromToken(jwt, loggedUser);

      return { ok: true, user: loggedUser };
    } catch (err) {
      console.error('Google login error:', err);
      setError(
        err.response?.data?.message ||
        'Google login failed.'
      );
      return { ok: false, error: err };
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  const updateMe = async (payload) => {
    // payload: { display_name?, password? }
    const res = await api.patch('/users/me', payload);
    // backend returns { message, user: {...} }
    setUser(res.data.user);
    return res.data.user;
  };

  const value = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    updateMe,
    loginWithGoogle,
    setSessionFromToken,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
